import { createSlice, PayloadAction } from "@reduxjs/toolkit";

interface User {
  id: number;
  email: string;
  role: string;
  avatar: string;
  name: string;
  allow_unblinding?: boolean;
}

interface AuthState {
  user: User | null;
  access_token: string | null;
  refresh_token: string | null;
  isAuthenticated: boolean;
}

function parseUserData(data: string | null): any | null {
  if (!data || data === "null" || data === "undefined") return null;
  try {
    return JSON.parse(data);
  } catch {
    return null;
  }
}

// Safe localStorage access for SSR
function getFromLocalStorage(key: string): string | null {
  if (typeof window === "undefined") return null;
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

const accessToken = getFromLocalStorage("access_token");
const refreshToken = getFromLocalStorage("refresh_token");
const userData = getFromLocalStorage("user");

const initialState: AuthState = {
  user: parseUserData(userData),
  access_token: accessToken,
  refresh_token: refreshToken,
  isAuthenticated: !!accessToken,
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    loginSuccess: (
      state,
      action: PayloadAction<{
        user: User;
        access_token: string;
        refresh_token: string;
      }>
    ) => {
      const { user, access_token, refresh_token } = action.payload;
      state.user = user;
      state.access_token = access_token;
      state.refresh_token = refresh_token;
      state.isAuthenticated = true;

      // Persist to localStorage (only on client side)
      if (typeof window !== "undefined") {
        localStorage.setItem("user", JSON.stringify(user));
        localStorage.setItem("access_token", access_token);
        localStorage.setItem("refresh_token", refresh_token);
      }
    },

    logout: (state) => {
      state.user = null;
      state.access_token = null;
      state.refresh_token = null;
      state.isAuthenticated = false;

      // Clear localStorage (only on client side)
      if (typeof window !== "undefined") {
        localStorage.removeItem("user");
        localStorage.removeItem("access_token");
        localStorage.removeItem("refresh_token");
        localStorage.removeItem("duplicateSearchResults");
        localStorage.removeItem("duplicateSearchFields");
        localStorage.removeItem("case-list-advanced-filters");
        localStorage.removeItem("line-listing-advanced-filters");
      }
    },
    clearAllState: () => {
      // This action will be caught by extraReducers in other slices
      // to reset their state when user logs out
    },
  },
});

export const { loginSuccess, logout, clearAllState } = authSlice.actions;

export default authSlice.reducer;

// Selectors
export const selectCurrentUser = (state: any) => state.auth.user;
export const selectAccessToken = (state: any) => state.auth.access_token;
export const selectIsAuthenticated = (state: any) => state.auth.isAuthenticated;
